import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from 'src/api/request-handler';
import { API_GET_ADDRESS_SUGGESTIONS } from 'src/api/request-objects';
import { stateCodeToFullName } from 'src/utils/state-mapper';

export type Suggestion = {
  street_line: string;
  secondary: string;
  city: string;
  state: string;
  zipcode: string;
  entries: number;
};

export type FPAddressFormFields = {
  address_number: string;
  address_street_name: string;
  apt_suite: string;
  city: string;
  state_name: string;
  zip_code: string;
};

export const NO_ADDRESS_FOUND_MESSAGE = 'No address found? CLICK HERE TO ENTER MANUALLY.';

export const TYPE_TO_SEARCH_MESSAGE = 'Type to search for an address.';

export const AUTOCOMPLETE_KEY_FIELD = 'address_number';

/**
 * The autocomplete input reuses address_number to display the entire address, so submitted
 * values can look like "29676 Rancho California Rd Temecula, CA 92591". Collapse it back to
 * the house number. Detected from the data (address_number contains the street name) rather
 * than from isAutocompleteEnabled, because several submit paths bypass that flag. A manually
 * typed house number never contains the street name, so it is left untouched.
 */
export const normalizeAddressNumberValues = <T extends Record<string, any>>(values: T): T => {
  if (!values) return values;
  const addressNumber = values[AUTOCOMPLETE_KEY_FIELD];
  const streetName = values.address_street_name;
  if (typeof addressNumber !== 'string' || !addressNumber.includes(' ')) return values;
  if (typeof streetName !== 'string' || !streetName || !addressNumber.includes(streetName)) return values;
  return { ...values, [AUTOCOMPLETE_KEY_FIELD]: addressNumber.trim().split(' ')[0] };
};

function buildAddress(suggestion: Suggestion) {
  // Do not mutate suggestion — the same object is later looked up by string match
  let secondary = suggestion.secondary || '';
  let whiteSpace = '';
  if (secondary) {
    if (suggestion.entries > 1) {
      secondary += ' (' + suggestion.entries + ' entries)';
    }
    whiteSpace = ' ';
  }
  return (
    suggestion.street_line +
    whiteSpace +
    secondary +
    ' ' +
    suggestion.city +
    ', ' +
    suggestion.state +
    ' ' +
    suggestion.zipcode
  );
}

export function useAddressSuggestions(searchAddress: string, searchByEmail: string) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const [hasSearched, setHasSearched] = useState(false);

  const placeholderText = [hasSearched ? NO_ADDRESS_FOUND_MESSAGE : TYPE_TO_SEARCH_MESSAGE];

  const suggestionsMapped = useMemo(
    () => (suggestions.length > 0 ? suggestions.map((suggestion) => buildAddress(suggestion)) : placeholderText),
    [suggestions],
  );

  const resetSuggestions = () => {
    setSuggestions([]);
    setHasSearched(false);
  };

  useEffect(() => {
    if (searchAddress.length === 0) {
      resetSuggestions();
    }
  }, [searchAddress]);

  useQuery({
    queryKey: ['address-suggestions', searchAddress, searchByEmail],
    queryFn: async () => {
      if (searchAddress.length === 0) {
        setSuggestions([]);
        return [];
      }

      const suggestionsFound = await apiRequest<Suggestion[]>(API_GET_ADDRESS_SUGGESTIONS, {
        query: {
          search: searchAddress,
          email: searchByEmail
        },
      });

      setSuggestions(suggestionsFound);

      setHasSearched(true);

      return suggestionsFound;
    },
    initialData: [],
    staleTime: 0, // Data immediately considered stale
    gcTime: 0,
  });

  const getAddressFormFieldsFromOption = (option: string): FPAddressFormFields | null => {
    const optionIndex = suggestionsMapped.findIndex((suggestion) => suggestion === option);

    const suggestion = suggestions[optionIndex];

    // null (not empty strings): callers must not overwrite existing city/state/zip with blanks
    if (!suggestion) {
      return null;
    }

    // Multi-entry secondary (e.g. apartment building) — keep refining; city/state not final yet
    if (suggestion.entries > 1) {
      return {
        address_number: suggestion.street_line.split(' ')[0],
        address_street_name: suggestion.street_line.split(' ').slice(1).join(' '),
        apt_suite: suggestion.secondary,
        city: '',
        state_name: '',
        zip_code: '',
      };
    }

    return {
      address_number: suggestion.street_line.split(' ')[0],
      address_street_name: suggestion.street_line.split(' ').slice(1).join(' '),
      apt_suite: suggestion.secondary,
      city: suggestion.city,
      state_name: stateCodeToFullName(suggestion.state),
      zip_code: suggestion.zipcode,
    };
  };

  return {
    suggestions: suggestionsMapped,
    resetSuggestions,
    getAddressFormFieldsFromOption,
  };
}
